export enum BugReportStatus {
    New = "new",
    Triaged = "triaged",
    InProgress = "in_progress",
    Resolved = "resolved",
    WontFix = "wont_fix",
    Duplicate = "duplicate",
}

export enum BugReportSeverity {
    Low = "low",
    Medium = "medium",
    High = "high",
    Critical = "critical",
}

export const BUG_REPORT_STATUS_LABELS: Record<string, string> = {
    [BugReportStatus.New]: "New",
    [BugReportStatus.Triaged]: "Triaged",
    [BugReportStatus.InProgress]: "In Progress",
    [BugReportStatus.Resolved]: "Resolved",
    [BugReportStatus.WontFix]: "Won't Fix",
    [BugReportStatus.Duplicate]: "Duplicate",
};

export const BUG_REPORT_SEVERITY_LABELS: Record<string, string> = {
    [BugReportSeverity.Low]: "Low",
    [BugReportSeverity.Medium]: "Medium",
    [BugReportSeverity.High]: "High",
    [BugReportSeverity.Critical]: "Critical",
};

/** One API call captured in the run-up to a report. */
export interface BugReportBreadcrumb {
    method: string;
    url: string;
    status: number;
    durationMs: number;
    at: string;
    /** >1 when the same call repeated back-to-back (typically a polling widget). */
    repeatCount?: number;
    /** Paste into System → Error Logs to find the server's side of this exact call. */
    requestId?: string;
    /** What the backend said, for non-2xx responses. */
    errorMessage?: string;
    /** What we sent (failed calls only). Sensitive fields are redacted client-side. */
    requestPayload?: string;
    /** What came back (failed calls only) — the whole body, not just the message. */
    responsePayload?: string;
}

/** One client error captured in the run-up to a report. */
export interface BugReportRecentError {
    level: string;
    message: string;
    at: string;
}

export interface BugReport {
    bugReportId: number;
    /** The reporting tab's session id — shared with that tab's web error log lines. */
    correlationId: string | null;
    title: string;
    description: string | null;
    severity: BugReportSeverity;
    status: BugReportStatus;
    route: string | null;
    userAgent: string | null;
    appVersion: string | null;
    hasScreenshot: boolean;
    breadcrumbs: BugReportBreadcrumb[] | null;
    recentErrors: BugReportRecentError[] | null;
    resolutionNotes: string | null;
    reportedById: number | null;
    reportedByName: string | null;
    assignedToId: number | null;
    assignedToName: string | null;
    createdAt: string;
    updatedAt: string | null;
}

export interface BugReportSearchCriteria {
    status: string;
    severity: string;
    keyword: string;
    page: number;
    pageSize: number;
}

/** One line out of a server / web / mobile log file. */
export interface CorrelatedLogEntry {
    timestamp: string;
    level: string;
    message: string;
    stack?: string;
    requestId?: string;
    sessionId?: string;
}

/**
 * Everything known about a report, assembled server-side so a triager reads one
 * page instead of cross-referencing four:
 *   serverErrors — stack traces behind the API calls that failed
 *   webErrors    — what broke in the reporter's browser, same session
 *   mobileErrors — the same, for the mobile app
 */
export interface BugReportInvestigation {
    report: BugReport;
    serverErrors: CorrelatedLogEntry[];
    webErrors: CorrelatedLogEntry[];
    mobileErrors: CorrelatedLogEntry[];
    searchedDates: string[];
    /** Client errors were bounded to this window — the run-up to the report, not the whole session. */
    windowFrom: string;
    windowTo: string;
}

export interface BugReportSearchResult {
    reports: BugReport[];
    totalCount: number;
}
